import { useEffect, useMemo, useSyncExternalStore } from "react";

const STORAGE_KEY = "creative-adapt:ai-processing";
const OWNER_KEY = "creative-adapt:ai-processing-owner";
const CHANGE_EVENT = "creative-adapt:ai-processing-change";
const DEFAULT_TTL_MS = 20 * 60 * 1000;
const CANVAS_BASE_RE = /^\/__[a-z0-9-]+-frontend(?=\/|$)/;
let indicatorSnapshotKey = "";
let indicatorSnapshotValue: AiProcessingState | null = null;

type AiProcessingState = {
  id: string;
  ownerId: string | null;
  sourcePath: string;
  detail?: string;
  progress?: number | null;
  progressStartedAt?: number;
  progressTarget?: number | null;
  progressDurationMs?: number;
  updatedAt: number;
  expiresAt: number;
};

type AiProcessingSignalOptions = {
  detail?: string;
  progress?: number | null;
  progressTarget?: number | null;
  progressDurationMs?: number;
  ttlMs?: number;
};

function currentPath(): string {
  if (typeof window === "undefined") return "";
  return normalizePath(window.location.pathname);
}

function normalizePath(path: string): string {
  return path.replace(CANVAS_BASE_RE, "") || "/";
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function currentOwnerId(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(OWNER_KEY);
}

export function setAiProcessingOwner(ownerId: string | null) {
  if (typeof window === "undefined") return;
  const nextOwner = ownerId || null;
  const prevOwner = currentOwnerId();
  if (prevOwner === nextOwner) return;
  if (nextOwner) {
    window.localStorage.setItem(OWNER_KEY, nextOwner);
  } else {
    window.localStorage.removeItem(OWNER_KEY);
  }
  window.localStorage.removeItem(STORAGE_KEY);
  emitChange();
}

function readState(): AiProcessingState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AiProcessingState>;
    if (
      typeof parsed.id !== "string" ||
      typeof parsed.sourcePath !== "string" ||
      (parsed.ownerId ?? null) !== currentOwnerId() ||
      typeof parsed.expiresAt !== "number" ||
      parsed.expiresAt <= Date.now()
    ) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return deriveProgress(parsed as AiProcessingState);
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function subscribe(callback: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const interval = window.setInterval(callback, 1000);
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener("storage", callback);
  window.addEventListener("popstate", callback);
  return () => {
    window.clearInterval(interval);
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener("storage", callback);
    window.removeEventListener("popstate", callback);
  };
}

function clampProgress(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(100, value));
}

function deriveProgress(state: AiProcessingState): AiProcessingState {
  const from = clampProgress(state.progress);
  const target = clampProgress(state.progressTarget);
  if (
    from == null ||
    target == null ||
    target <= from ||
    typeof state.progressStartedAt !== "number" ||
    typeof state.progressDurationMs !== "number" ||
    state.progressDurationMs <= 0
  ) {
    return { ...state, progress: from };
  }
  const elapsed = Math.max(0, Date.now() - state.progressStartedAt);
  const ratio = Math.min(1, elapsed / state.progressDurationMs);
  const eased = 1 - Math.pow(1 - ratio, 2);
  return {
    ...state,
    progress: from + (target - from) * eased,
  };
}

function writeState(
  id: string,
  sourcePath: string,
  options: AiProcessingSignalOptions = {},
) {
  if (typeof window === "undefined") return;
  const now = Date.now();
  const progress = clampProgress(options.progress);
  const progressTarget = clampProgress(options.progressTarget);
  const state: AiProcessingState = {
    id,
    ownerId: currentOwnerId(),
    sourcePath,
    detail: options.detail,
    progress,
    progressStartedAt: now,
    progressTarget,
    progressDurationMs: options.progressDurationMs,
    updatedAt: now,
    expiresAt: now + (options.ttlMs ?? DEFAULT_TTL_MS),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  emitChange();
}

function clearState(id: string) {
  const state = readState();
  if (!state || state.id !== id) return;
  window.localStorage.removeItem(STORAGE_KEY);
  emitChange();
}

export function clearAiProcessingSignal(idParts: unknown[]) {
  const id = idParts.map((part) => String(part ?? "")).join(":");
  if (!id) return;
  clearState(id);
}

export function useAiProcessingSignal(
  active: boolean,
  idParts: unknown[],
  options: AiProcessingSignalOptions = {},
) {
  const id = useMemo(
    () => idParts.map((part) => String(part ?? "")).join(":"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    idParts,
  );
  const detail = options.detail;
  const progress = options.progress;
  const progressTarget = options.progressTarget;
  const progressDurationMs = options.progressDurationMs;
  const ttlMs = options.ttlMs;

  useEffect(() => {
    if (!id) return;
    if (active) {
      writeState(id, currentPath(), {
        detail,
        progress,
        progressTarget,
        progressDurationMs,
        ttlMs,
      });
    } else {
      clearState(id);
    }
  }, [active, detail, id, progress, progressDurationMs, progressTarget, ttlMs]);
}

export function useAiProcessingIndicatorState(
  location: string,
): AiProcessingState | null {
  return useSyncExternalStore(
    subscribe,
    () => {
      const state = readState();
      if (!state) return null;
      const normalizedLocation = normalizePath(location);
      if (normalizedLocation === state.sourcePath) return null;
      const progressKey =
        state.progress == null ? "null" : state.progress.toFixed(1);
      const key = [
        normalizedLocation,
        state.id,
        state.sourcePath,
        state.detail ?? "",
        progressKey,
        state.expiresAt,
      ].join("|");
      if (key === indicatorSnapshotKey) return indicatorSnapshotValue;
      indicatorSnapshotKey = key;
      indicatorSnapshotValue = state;
      return state;
    },
    () => null,
  );
}

export function useShouldShowAiProcessingIndicator(location: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => {
      const state = readState();
      if (!state) return false;
      return normalizePath(location) !== state.sourcePath;
    },
    () => false,
  );
}
